const DEFAULT_MAP_ID = 'streets-v2';
const DEFAULT_ZOOM = 16;
const DEFAULT_WIDTH = 600;
const DEFAULT_HEIGHT = 450;

type CoordinateSource = {
  source_record_only?: boolean;
  latitude?: number | string | null;
  longitude?: number | string | null;
  lat?: number | string | null;
  lng?: number | string | null;
  lon?: number | string | null;
};

type MapSnapshotOptions = {
  width?: number;
  height?: number;
  zoom?: number;
  mapId?: string;
  absolute?: boolean;
};

const asCoordinate = (value: unknown, minimum: number, maximum: number): number | null => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric < minimum || numeric > maximum) return null;
  return numeric;
};

const asDimension = (value: unknown, fallback: number): number => {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isInteger(numeric) || numeric < 1 || numeric > 2048) return fallback;
  return numeric;
};

/**
 * Build a cached CityScope map snapshot URL for a verified application point.
 * The endpoint composes ordinary MapTiler raster tiles server-side because
 * the current provider plan does not include MapTiler Static Maps.
 */
export const getMapSnapshotUrl = (
  application?: CoordinateSource | null,
  options: MapSnapshotOptions = {},
): string | null => {
  const apiKey = String(process.env.NEXT_PUBLIC_MAPTILER_API_KEY || '').trim();
  if (!apiKey || !application || application.source_record_only === true) return null;

  const latitude = asCoordinate(application.latitude ?? application.lat, -90, 90);
  const longitude = asCoordinate(application.longitude ?? application.lng ?? application.lon, -180, 180);
  if (latitude === null || longitude === null) return null;

  const mapId = String(options.mapId || process.env.NEXT_PUBLIC_MAPTILER_STATIC_MAP_ID || DEFAULT_MAP_ID)
    .trim()
    .replace(/[^a-z0-9_-]/gi, '');
  if (!mapId) return null;

  const zoom = Number.isFinite(Number(options.zoom))
    ? Math.round(Math.max(0, Math.min(22, Number(options.zoom))))
    : DEFAULT_ZOOM;
  const width = asDimension(options.width, DEFAULT_WIDTH);
  const height = asDimension(options.height, DEFAULT_HEIGHT);
  const snapshotPath = `/api/planning-map/snapshot?${new URLSearchParams({
    lat: String(latitude),
    lng: String(longitude),
    zoom: String(zoom),
    width: String(width),
    height: String(height),
    map: mapId,
  }).toString()}`;

  if (!options.absolute) return snapshotPath;

  const configuredSiteUrl = String(
    process.env.NEXT_PUBLIC_SITE_URL ||
      process.env.SITE_URL ||
      (process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : '') ||
      'https://www.city-scope.co.uk',
  ).replace(/\/+$/, '');

  return `${configuredSiteUrl}${snapshotPath}`;
};

export const MAP_SNAPSHOT_ATTRIBUTION = '\u00a9 MapTiler \u00a9 OpenStreetMap contributors';
